import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { API, PHASES, STATUSES, emptyEngagement } from "@/lib/engagements";
import { useAuth } from "@/context/AuthContext";

export const EngagementDrawer = ({ open, onOpenChange, engagement, onSaved }) => {
  const { user } = useAuth();
  const readOnly = user?.role === "supervisor";
  const [form, setForm] = useState(emptyEngagement());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const base = emptyEngagement();
    if (engagement) {
      setForm({
        ...base,
        ...engagement,
        notes: engagement.notes || "",
        year_end: engagement.year_end || "",
        status_override: engagement.status_override || "",
        services: { ...base.services, ...(engagement.services || {}) },
        account: { ...base.account, ...(engagement.account || {}) },
        audit: { ...base.audit, ...(engagement.audit || {}) },
        tax: { ...base.tax, ...(engagement.tax || {}) },
      });
    } else {
      setForm(base);
    }
  }, [open, engagement]);

  const setPhase = (phase, field, value) =>
    setForm((f) => ({ ...f, [phase]: { ...f[phase], [field]: value } }));

  const clean = (obj) =>
    Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, v === "" ? null : v]));

  const save = async () => {
    if (!form.client.trim()) {
      toast.error("Client name is required");
      return;
    }
    setSaving(true);
    const payload = {
      client: form.client.trim(),
      year_end: form.year_end || null,
      notes: form.notes || null,
      status_override: form.status_override || null,
      services: form.services,
      account: clean(form.account),
      audit: clean(form.audit),
      tax: clean(form.tax),
    };
    try {
      if (engagement?.id) {
        await axios.put(`${API}/engagements/${engagement.id}`, payload);
        toast.success("Engagement updated");
      } else {
        await axios.post(`${API}/engagements`, payload);
        toast.success("Engagement created");
      }
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error("Could not save engagement");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await axios.delete(`${API}/engagements/${engagement.id}`);
      toast.success("Engagement deleted");
      onOpenChange(false);
      onSaved();
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="engagement-drawer"
        className="bg-white w-full sm:max-w-xl p-0 flex flex-col"
      >
        <SheetHeader className="shrink-0 border-b border-border px-6 py-5">
          <SheetTitle className="font-display text-2xl font-extrabold">
            {engagement ? "Edit engagement" : "New engagement"}
          </SheetTitle>
          <SheetDescription className="text-xs text-zinc-500">
            Track Accounting, Audit and Taxation stages for this client year end.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Client</Label>
              <Input
                data-testid="input-client"
                className="rounded-sm mt-1.5"
                value={form.client}
                onChange={(e) => setForm({ ...form, client: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Year End</Label>
              <Input
                data-testid="input-year-end"
                className="rounded-sm mt-1.5"
                placeholder="2025-12-31 or Jan - Jun 25"
                value={form.year_end}
                onChange={(e) => setForm({ ...form, year_end: e.target.value })}
              />
            </div>
          </div>

          {PHASES.map((phase) => {
            const enabled = form.services?.[phase.key] !== false;
            return (
            <div key={phase.key} className={`border border-border ${enabled ? "" : "opacity-60"}`}>
              <div className="flex items-center justify-between bg-zinc-900 px-4 py-2">
                <span className="text-xs uppercase tracking-widest text-white font-semibold">
                  {phase.label}
                </span>
                <label className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-300">
                  <Checkbox
                    data-testid={`toggle-service-${phase.key}`}
                    checked={enabled}
                    onCheckedChange={(v) =>
                      setForm((f) => ({ ...f, services: { ...f.services, [phase.key]: Boolean(v) } }))
                    }
                    className="border-zinc-500 data-[state=checked]:bg-white data-[state=checked]:text-zinc-900"
                  />
                  Applicable
                </label>
              </div>
              {enabled ? (
              <div className="p-4 space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">
                    {phase.label} PIC
                  </Label>
                  <Input
                    data-testid={`input-${phase.key}-pic`}
                    className="rounded-sm mt-1.5"
                    value={form[phase.key].pic || ""}
                    onChange={(e) => setPhase(phase.key, "pic", e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {phase.fields.map(([f, label]) => (
                    <div key={f}>
                      <Label className="text-xs text-zinc-500">{label}</Label>
                      <Input
                        type="date"
                        data-testid={`input-${phase.key}-${f}`}
                        className="rounded-sm mt-1.5 tabular"
                        value={form[phase.key][f] || ""}
                        onChange={(e) => setPhase(phase.key, f, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              </div>
              ) : (
                <div className="px-4 py-3 text-xs text-zinc-500">
                  Not applicable for this client — excluded from status and workload.
                </div>
              )}
            </div>
            );
          })}

          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">
              Status override (optional)
            </Label>
            <select
              data-testid="select-status-override"
              className="mt-1.5 w-full border border-input bg-white rounded-sm h-10 px-3 text-sm"
              value={form.status_override || ""}
              onChange={(e) => setForm({ ...form, status_override: e.target.value })}
            >
              <option value="">Auto (from milestone dates)</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Notes</Label>
            <Textarea
              data-testid="input-notes"
              className="rounded-sm mt-1.5"
              value={form.notes || ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
        </div>

        <div className="shrink-0 flex items-center justify-between gap-3 border-t border-border bg-white px-6 py-4">
          {engagement && !readOnly ? (
            <Button
              data-testid="delete-engagement-btn"
              variant="ghost"
              onClick={remove}
              className="rounded-sm text-red-700 hover:bg-red-50 hover:text-red-800"
            >
              Delete
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-3">
            <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>
              {readOnly ? "Close" : "Cancel"}
            </Button>
            {!readOnly && (
              <Button
                data-testid="save-engagement-btn"
                onClick={save}
                disabled={saving}
                className="rounded-sm bg-black text-white hover:bg-zinc-800 hover:text-white"
              >
                {saving ? "Saving…" : "Save engagement"}
              </Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
